
import { Component, Prop, Vue } from 'vue-property-decorator';
import { AxiosError } from 'axios';
import { deleteJobImage } from '@/api';
import Button from '@/components/UI/button/Button.vue';
import Icon from '@/components/UI/Icon.vue';
import { notificationErrorText } from '@/utils/PortalConstants';

interface AppImage {
  path: string;
  source: string;
  created_at: string;
  ref: string;
}

interface JobStop {
  app_images?: AppImage[];
  [key: string]: any;
}

@Component({
  components: {
    Button,
    Icon,
  },
})
export default class JobDetailedImages extends Vue {
  @Prop({ type: Array, required: true }) readonly stops!: JobStop[];
  @Prop({ type: Number, required: true }) readonly jobId!: number;
  @Prop({ type: String, required: true }) readonly jobDate!: string;

  public previewDialog = false;
  public previewImagePath = '';
  public deleteDialog = false;
  public isDeleting = false;
  private imageToDelete: { path: string; stopIdx: number; imageIdx: number } | null = null;

  public hasImages(): boolean {
    return this.stops.some(stop => stop.app_images && stop.app_images.length > 0);
  }

  public openImagePreview(imagePath: string): void {
    this.previewImagePath = imagePath;
    this.previewDialog = true;
  }

  public closeImagePreview(): void {
    this.previewDialog = false;
    this.previewImagePath = '';
  }

  public openDeleteDialog(imagePath: string, stopIdx: number, imageIdx: number): void {
    this.imageToDelete = { path: imagePath, stopIdx, imageIdx };
    this.deleteDialog = true;
  }

  public closeDeleteDialog(): void {
    this.deleteDialog = false;
    this.imageToDelete = null;
  }

  private extractImagePath(fullPath: string): string {
    const pathWithoutQuery = fullPath.split('?')[0];

    // Test server: dashtest-images/rhApp/...
    const dashtestMatch = pathWithoutQuery.match(/(dashtest-images\/rhApp\/.+)$/);
    if (dashtestMatch) {
      return decodeURIComponent(dashtestMatch[1]);
    }

    // Production: images/rhApp/...
    const imagesMatch = pathWithoutQuery.match(/(images\/rhApp\/.+)$/);
    if (imagesMatch) {
      return decodeURIComponent(imagesMatch[1]);
    }

    // Production: rhApp/...
    const rhAppMatch = pathWithoutQuery.match(/(rhApp\/.+)$/);
    if (rhAppMatch) {
      return decodeURIComponent(rhAppMatch[1]);
    }

    // Fallback for paths that already start with the expected prefix
    if (
      pathWithoutQuery.startsWith('dashtest-images/') ||
      pathWithoutQuery.startsWith('images/') ||
      pathWithoutQuery.startsWith('rhApp/')
    ) {
      return decodeURIComponent(pathWithoutQuery);
    }

    return fullPath;
  }

  public confirmDelete(): void {
    if (!this.imageToDelete) {
      return;
    }
    this.isDeleting = true;

    const imageToDelete = this.imageToDelete;
    const imagePath = this.extractImagePath(imageToDelete.path);
    deleteJobImage(this.jobId, this.jobDate, imagePath)
      .then(() => {
        this.$emit('image-deleted', {
          stopIdx: imageToDelete.stopIdx,
          imageIdx: imageToDelete.imageIdx,
        });

        this.$notify({
          group: 'network',
          text: 'Image successfully deleted',
          type: 'success',
          duration: 3000,
        });

        this.closeDeleteDialog();
      })
      .catch(err => {
        const axiosError = err as AxiosError<{ detail?: string }>;
        this.$notify({
          group: 'network',
          title: 'Error deleting the image',
          text: axiosError.response?.data?.detail || notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.isDeleting = false;
      });
  }
}
